import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVideoPopupConfig, type VideoPopupConfig } from '@/pages/admin/components/VideoPopupPanel';

const SESSION_KEY = 'video_popup_shown';
export const VIDEO_ANALYTICS_KEY = 'video_popup_analytics';

export interface VideoAnalytics {
  totalViews: number;
  totalClicks: number;
  lastUpdated: number;
  dailyStats: { date: string; views: number; clicks: number }[];
}

export function getVideoAnalytics(): VideoAnalytics {
  try {
    const saved = localStorage.getItem(VIDEO_ANALYTICS_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return { totalViews: 0, totalClicks: 0, lastUpdated: Date.now(), dailyStats: [] };
}

/** Check directly from sessionStorage — no async, no state race */
function isAlreadyShown(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Mark as shown in sessionStorage immediately */
function markAsShown(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch { /* ignore */ }
}

// Global debug helper
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).videoPopupDebug = {
    forceShow: () => {
      try { 
        sessionStorage.removeItem(SESSION_KEY); 
        console.log('[VideoPopup] Session cleared, reloading...');
      } catch { /* ignore */ }
      window.location.reload();
    },
    clearSession: () => {
      try { 
        sessionStorage.removeItem(SESSION_KEY); 
        console.log('[VideoPopup] Session cleared, refresh to see popup');
      } catch { /* ignore */ }
    },
    getConfig: getVideoPopupConfig,
    getAnalytics: getVideoAnalytics,
    checkStatus: () => {
      const cfg = getVideoPopupConfig();
      const shown = isAlreadyShown();
      console.log('[VideoPopup] Config:', cfg);
      console.log('[VideoPopup] Already shown:', shown);
      return { config: cfg, alreadyShown: shown };
    },
  };
}

function recordView() {
  try {
    const analytics = getVideoAnalytics();
    const today = new Date().toISOString().split('T')[0];
    const dailyStats = [...analytics.dailyStats];
    const todayIndex = dailyStats.findIndex(d => d.date === today);
    if (todayIndex >= 0) {
      dailyStats[todayIndex] = { ...dailyStats[todayIndex], views: dailyStats[todayIndex].views + 1 };
    } else {
      dailyStats.push({ date: today, views: 1, clicks: 0 });
    }
    const updated: VideoAnalytics = {
      totalViews: analytics.totalViews + 1,
      totalClicks: analytics.totalClicks,
      lastUpdated: Date.now(),
      dailyStats: dailyStats.slice(-30),
    };
    localStorage.setItem(VIDEO_ANALYTICS_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

function recordClick() {
  try {
    const analytics = getVideoAnalytics();
    const today = new Date().toISOString().split('T')[0];
    const dailyStats = [...analytics.dailyStats];
    const todayIndex = dailyStats.findIndex(d => d.date === today);
    if (todayIndex >= 0) {
      dailyStats[todayIndex] = { ...dailyStats[todayIndex], clicks: dailyStats[todayIndex].clicks + 1 };
    } else {
      dailyStats.push({ date: today, views: 0, clicks: 1 });
    }
    const updated: VideoAnalytics = {
      totalViews: analytics.totalViews,
      totalClicks: analytics.totalClicks + 1,
      lastUpdated: Date.now(),
      dailyStats: dailyStats.slice(-30),
    };
    localStorage.setItem(VIDEO_ANALYTICS_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

export default function VideoPopup() {
  const [config, setConfig] = useState<VideoPopupConfig | null>(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasRunRef = useRef(false);

  // Load config and schedule popup
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    try {
      const cfg = getVideoPopupConfig();
      const urlParams = new URLSearchParams(window.location.search);
      const forceShow = urlParams.get('force_video_popup') === '1' || urlParams.get('show_video') === '1';
      
      console.log('[VideoPopup] Config loaded:', cfg);
      console.log('[VideoPopup] Force show:', forceShow);
      console.log('[VideoPopup] Already shown:', isAlreadyShown());
      
      if (forceShow && cfg) {
        sessionStorage.removeItem(SESSION_KEY);
        setConfig({ ...cfg, enabled: true });
      } else {
        setConfig(cfg);
      }
    } catch (e) {
      console.error('[VideoPopup] Error:', e);
    }
  }, []);

  // Schedule popup when config is ready
  useEffect(() => {
    if (!config) return;

    // Check if should show
    if (!config.enabled || !config.videoUrl) {
      console.log('[VideoPopup] Not showing - disabled or no video');
      return;
    }

    // Check if already shown (only if showOncePerSession is true)
    if (config.showOncePerSession && isAlreadyShown()) {
      console.log('[VideoPopup] Not showing - already shown (showOncePerSession=true)');
      return;
    }

    console.log('[VideoPopup] Scheduling popup in 3s... (showOncePerSession=' + config.showOncePerSession + ')');
    
    timerRef.current = setTimeout(() => {
      // Double check for showOncePerSession mode
      if (config.showOncePerSession && isAlreadyShown()) {
        console.log('[VideoPopup] Skipping - marked as shown during wait');
        return;
      }
      
      console.log('[VideoPopup] Showing popup now!');
      
      // Only mark as shown if showOncePerSession is true
      if (config.showOncePerSession) {
        markAsShown();
      }
      
      setVisible(true);
      recordView();
    }, 3000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [config]);

  // Play video when visible
  useEffect(() => {
    if (!visible || !videoRef.current) return;
    const vid = videoRef.current;
    const tryPlay = async () => {
      try {
        vid.muted = true;
        await vid.play();
      } catch (e) {
        console.log('[VideoPopup] Autoplay blocked:', e);
      }
    };
    const t = setTimeout(tryPlay, 100);
    return () => clearTimeout(t);
  }, [visible]);

  const dismiss = useCallback(() => {
    // Only mark as shown if showOncePerSession is true
    if (config?.showOncePerSession) {
      markAsShown();
    }
    setClosing(true);
    if (videoRef.current) videoRef.current.pause();
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 280);
  }, [config?.showOncePerSession]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismiss();
  };

  const handleVideoClick = () => {
    if (!config?.productLink) return;
    recordClick();
    dismiss();
    setTimeout(() => navigate(config.productLink), 300);
  };

  if (!visible || !config) {
    return null;
  }

  const popupWidth = config.popupWidth ?? 160;
  const corner = config.corner ?? 'bottom-left';
  const posClass =
    corner === 'bottom-left' ? 'bottom-6 left-4' :
    corner === 'bottom-right' ? 'bottom-6 right-4' :
    corner === 'top-left' ? 'top-20 left-4' :
    'top-20 right-4';

  return (
    <div
      className={`fixed ${posClass} z-[80] transition-all duration-300 ${closing ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}
    >
      <div style={{ width: `${popupWidth}px`, height: `${popupWidth}px` }} className="relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-all cursor-pointer"
        >
          <i className="ri-close-line text-xs"></i>
        </button>

        {/* Video container */}
        <div
          className={`w-full h-full rounded-xl overflow-hidden border border-gray-100 bg-black relative ${config.productLink ? 'cursor-pointer' : ''}`}
          onClick={config.productLink ? handleVideoClick : undefined}
        >
          <video
            ref={videoRef}
            src={config.videoUrl}
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            onError={() => setVideoError(true)}
          />

          {config.productLink && (
            <div className="absolute bottom-1.5 left-0 right-0 flex justify-center pointer-events-none z-10">
              <span className="bg-black/60 text-white rounded-full px-2 py-0.5 backdrop-blur-sm" style={{ fontSize: '9px' }}>
                tap to view product
              </span>
            </div>
          )}

          {videoError && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                videoRef.current?.play().then(() => setVideoError(false)).catch(() => {});
              }}
            >
              <i className="ri-play-circle-line text-white text-2xl mb-1"></i>
              <span className="text-white text-xs">Tap to play</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
